<template>
    <!-- ── casting variant ────────────────────────────────────────────── -->
    <div
        v-if="variant === 'casting'"
        @click="$emit('click')"
        class="relative w-full min-h-[320px] bg-white/[0.03] border border-amber-500/25 rounded-2xl p-6 backdrop-blur-xl flex flex-col cursor-pointer group overflow-hidden hover:-translate-y-1 transition-transform duration-300 casting-glow"
    >
        <div class="flex justify-between items-start mb-4 relative z-10">
            <div class="flex -space-x-2">
                <div v-for="(c, i) in pitch.cast.slice(0, 4)">
                    <div
                        v-if="!c.thumb"
                        :key="i + '-noimage-' + c.name"
                        :class="`w-8 h-8 rounded-full ${c.color} flex items-center justify-center text-[10px] font-bold text-white border-2 border-[rgb(20,20,20)] shadow-sm`"
                        :title="c.name"
                    >{{ initials(c.name) }}</div>
                    <img
                        v-else
                        :key="i + '-image-' + c.name"
                        :src="c.thumb"
                        :alt="c.name"
                        class="w-8 h-8 rounded-full border-2 border-[rgb(20,20,20)] object-cover shadow-sm"
                        :title="c.name"
                    />
                </div>
            </div>
            <div class="flex items-center gap-1.5 bg-amber-500/15 text-amber-400 text-[10px] uppercase font-bold px-2.5 py-1 rounded-full tracking-wider border border-amber-500/25">
                <Video class="w-3 h-3" />
                Now Casting
            </div>
        </div>

        <div class="relative z-10 flex-1">
            <h3 class="text-xl font-bold text-white mb-2 leading-tight group-hover:text-amber-300 transition-colors">
                {{ pitch.title }}
            </h3>
            <p class="text-[14px] text-white/70 line-clamp-2 leading-relaxed">{{ pitch.setup }}</p>
        </div>

        <div class="mt-6 space-y-4 relative z-10">
            <div class="flex items-center justify-between text-[12px] font-medium">
                <div class="flex items-center gap-1.5 text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20">
                    <CheckCircle class="w-3.5 h-3.5" />
                    <span>Member Supported</span>
                </div>
                <div v-if="pitch.auditionCount > 0" class="flex items-center gap-1.5 text-white/60 bg-white/5 px-2.5 py-1 rounded-lg">
                    <Play class="w-3.5 h-3.5" />
                    <span>{{ pitch.auditionCount }} audition{{ pitch.auditionCount === 1 ? '' : 's' }} so far</span>
                </div>
            </div>
            <div class="flex items-center gap-1 text-[11px] font-semibold" :class="pitch.netScore > 0 ? 'text-emerald-400' : pitch.netScore < 0 ? 'text-red-400' : 'text-white/30'">
                <span>{{ pitch.netScore > 0 ? '▲' : pitch.netScore < 0 ? '▼' : '–' }}</span>
                <span>{{ Math.abs(pitch.netScore) }} votes</span>
            </div>
        </div>
    </div>

    <!-- ── featured / in-race variant ───────────────────────────────── -->
    <div
        v-else-if="variant === 'featured'"
        @click="$emit('click')"
        class="relative w-full min-h-[320px] bg-white/[0.03] border border-violet-500/20 rounded-2xl p-6 backdrop-blur-xl flex flex-col cursor-pointer group hover:-translate-y-1 transition-transform duration-300 featured-glow"
    >
        <div class="flex justify-between items-start mb-4 relative z-10">
            <div class="flex -space-x-2">
                <div v-for="(c, i) in pitch.cast.slice(0, 4)">
                    <div
                        v-if="!c.thumb"
                        :key="i"
                        :class="`w-8 h-8 rounded-full ${c.color} flex items-center justify-center text-[10px] font-bold text-white border-2 border-[rgb(20,20,20)] shadow-sm`"
                        :title="c.name"
                    >{{ initials(c.name) }}</div>
                    <img
                        v-else
                        :key="i + '-image-' + c.name"
                        :src="c.thumb"
                        :alt="c.name"
                        class="w-8 h-8 rounded-full border-2 border-[rgb(20,20,20)] object-cover shadow-sm"
                        :title="c.name"
                    />
                </div>
                <div
                    v-if="pitch.cast.length > 4"
                    class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-white border-2 border-[rgb(20,20,20)]"
                >+{{ pitch.cast.length - 4 }}</div>
            </div>
            <div class="bg-violet-500 text-white text-[10px] uppercase font-bold px-2.5 py-1 rounded-full tracking-wider shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                In the Race
            </div>
        </div>

        <div class="relative z-10 flex-1">
            <h3 class="text-xl font-bold text-white mb-2 leading-tight group-hover:text-violet-300 transition-colors">
                {{ pitch.title }}
            </h3>
            <p class="text-[14px] text-white/70 line-clamp-2 leading-relaxed">{{ pitch.setup }}</p>
        </div>

        <div class="mt-6 space-y-4 relative z-10">
            <div class="flex items-center justify-between text-[12px] font-medium">
                <div class="flex items-center gap-1.5 text-white/80 bg-white/5 px-2.5 py-1 rounded-lg">
                    <span>{{ pitch.stars }}/{{ pitch.starGoal }} backed</span>
                </div>
                <div v-if="pitch.auditionCount > 0" class="flex items-center gap-1.5 text-violet-300 bg-violet-500/10 px-2.5 py-1 rounded-lg border border-violet-500/20">
                    <Play class="w-3.5 h-3.5" />
                    <span>{{ pitch.auditionCount }} Auditions</span>
                </div>
            </div>

            <div v-if="pitch.leadingAuditionScore" class="space-y-1.5">
                <div class="flex justify-between text-[11px] font-semibold text-white/50 uppercase tracking-wider">
                    <span>Leading Audition</span>
                    <span class="text-violet-400">{{ pitch.leadingAuditionScore }} / 800</span>
                </div>
                <div class="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <div
                        class="h-full bg-violet-500 rounded-full transition-all duration-1000"
                        :style="{ width: Math.min(100, (pitch.leadingAuditionScore / 800) * 100) + '%' }"
                    />
                </div>
            </div>
        </div>
    </div>

    <!-- ── medium / trending variant ─────────────────────────────────── -->
    <div
        v-else-if="variant === 'medium'"
        @click="$emit('click')"
        class="w-[280px] sm:w-[320px] shrink-0 bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:bg-white/[0.04] hover:border-white/[0.08] transition-all cursor-pointer flex flex-col group hover:-translate-y-1 duration-300"
    >
        <div class="flex justify-between items-start mb-3">
            <div class="flex -space-x-1.5">
                <div v-for="(c, i) in pitch.cast.slice(0, 3)">
                    <div
                        v-if="!c.thumb"
                        :key="i"
                        :class="`w-6 h-6 rounded-full ${c.color} flex items-center justify-center text-[8px] font-bold text-white border-2 border-[rgb(20,20,20)]`"
                        :title="c.name"
                    >{{ initials(c.name) }}</div>
                    <img
                        v-else
                        :key="i + '-image-' + c.name"
                        :src="c.thumb"
                        :alt="c.name"
                        class="w-6 h-6 rounded-full border-2 border-[rgb(20,20,20)] object-cover"
                        :title="c.name"
                    />
                </div>
            </div>
        </div>

        <div class="flex-1 mb-4">
            <h3 class="text-[16px] font-semibold text-white mb-1.5 leading-tight group-hover:text-amber-400 transition-colors">
                {{ pitch.title }}
            </h3>
            <p class="text-[13px] text-white/60 line-clamp-2 leading-relaxed">{{ pitch.setup }}</p>
        </div>

        <div class="flex items-center gap-1 text-[11px] font-semibold pt-3 border-t border-white/[0.06]" :class="pitch.netScore >= 0 ? 'text-emerald-400' : 'text-red-400'">
            <span>{{ pitch.netScore >= 0 ? '▲' : '▼' }}</span>
            <span>{{ Math.abs(pitch.netScore) }} votes</span>
        </div>

    </div>

    <!-- ── compact / fresh variant ───────────────────────────────────── -->
    <div
        v-else
        @click="$emit('click')"
        class="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.04] hover:border-white/[0.1] transition-all cursor-pointer flex flex-col group h-full hover:-translate-y-1 duration-300"
    >
        <div class="flex justify-between items-start mb-3">
            <div class="bg-emerald-500/20 text-emerald-400 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wider">
                New
            </div>
            <div class="flex -space-x-1">
                <div v-for="(c, i) in pitch.cast.slice(0, 3)">
                    <div
                        v-if="!c.thumb"
                        :key="i"
                        :class="`w-5 h-5 rounded-full ${c.color} flex items-center justify-center text-[7px] font-bold text-white border border-[rgb(20,20,20)]`"
                        :title="c.name"
                    >{{ initials(c.name) }}</div>
                    <img
                        v-else
                        :key="i + '-image-' + c.name"
                        :src="c.thumb"
                        :alt="c.name"
                        class="w-5 h-5 rounded-full border border-[rgb(20,20,20)] object-cover"
                        :title="c.name"
                    />
                </div>
            </div>
        </div>

        <h3 class="text-[15px] font-semibold text-white mb-4 leading-snug group-hover:text-emerald-400 transition-colors line-clamp-2 flex-1">
            {{ pitch.title }}
        </h3>

        <div class="flex items-center gap-1 text-[11px] font-semibold pt-3 border-t border-white/[0.04]" :class="pitch.netScore >= 0 ? 'text-emerald-400' : 'text-red-400'">
            <span>{{ pitch.netScore >= 0 ? '▲' : '▼' }}</span>
            <span>{{ Math.abs(pitch.netScore) }} votes</span>
        </div>

    </div>
</template>

<script setup>
import { Play, Video, CheckCircle } from 'lucide-vue-next'

defineProps({
    pitch:   { type: Object, required: true },
    variant: { type: String, default: 'compact' }, // casting | featured | medium | compact
})

defineEmits(['click'])

function initials(name) {
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
</script>

<style scoped>
@keyframes casting-pulse {
    0%, 100% { box-shadow: 0 0 20px rgba(245,158,11,0.08); }
    50%       { box-shadow: 0 0 40px rgba(245,158,11,0.20); }
}
@keyframes featured-pulse {
    0%, 100% { box-shadow: 0 0 20px rgba(139,92,246,0.10); }
    50%       { box-shadow: 0 0 40px rgba(139,92,246,0.25); }
}
.casting-glow  { animation: casting-pulse  4s ease-in-out infinite; }
.featured-glow { animation: featured-pulse 3s ease-in-out infinite; }
</style>
